import { config } from "@/config/env";
import { 
    type ListSubscriptionsRequest, 
    type ListSubscriptionsResponse, 
    type SubscriptionStatsResponse,
    type Transaction,
    type ListPlansResponse,
    type CreatePlanRequest,
    type UpdatePlanRequest,
    type Plan
} from "@/types/subscriptions";

const API_BASE_URL = config.apiUrl;

async function fetchWithAuth(path: string, token: string, options: RequestInit = {}) {
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...options.headers,
  };
  
  const response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API Error: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
}

export const subscriptionApi = {
  listAll: (token: string, params: ListSubscriptionsRequest = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status && params.status !== "all") queryParams.append("status", params.status);
    if (params.planName) queryParams.append("planName", params.planName);
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.offset) queryParams.append("offset", params.offset.toString());

    const query = queryParams.toString();
    const url = `/admin/subscriptions${query ? `?${query}` : ""}`;
    
    return fetchWithAuth(url, token).then(res => res as ListSubscriptionsResponse);
  },

  getStats: (token: string) => 
    fetchWithAuth("/admin/subscriptions/stats", token).then(res => res as SubscriptionStatsResponse),

  getRecentTransactions: (token: string) => 
    fetchWithAuth("/admin/subscriptions/transactions", token).then(res => (res as { transactions: Transaction[] }).transactions),

  // Plans
  listPlans: (token: string) => 
    fetchWithAuth("/admin/plans", token).then(res => res as ListPlansResponse),

  createPlan: (token: string, data: CreatePlanRequest) => 
    fetchWithAuth("/admin/plans", token, {
      method: "POST",
      body: JSON.stringify(data),
    }).then(res => res as Plan),

  updatePlan: (token: string, id: string, data: UpdatePlanRequest) => 
    fetchWithAuth(`/admin/plans/${id}`, token, {
      method: "PUT",
      body: JSON.stringify(data),
    }).then(res => res as Plan),

  syncPlans: (token: string) =>
    fetchWithAuth("/admin/plans/sync", token, {
      method: "POST",
    }).then(res => res as { synced: number; errors: string[] }),
};
